import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ApolloGqlAccountManager } from '@bloomays-lib/types.shared';

export type BloomiesState = {
  bloomies: Record<string, ApolloGqlAccountManager>;
  fetchedAtByEmail: Record<string, number>;
  setBloomie: (email: string, bloomie: ApolloGqlAccountManager) => void;
  getByEmail: (email: string) => ApolloGqlAccountManager | undefined;
  mustRefreshBloomie: (email: string) => boolean;
  reset: () => void;
};

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day TTL

export const useBloomiesStore = create<BloomiesState>()(
  persist(
    (set, get) => ({
      bloomies: {},
      fetchedAtByEmail: {},

      setBloomie: (email: string, bloomie: ApolloGqlAccountManager) =>
        set((state) => ({
          bloomies: { ...state.bloomies, [email]: bloomie },
          fetchedAtByEmail: { ...state.fetchedAtByEmail, [email]: Date.now() },
        })),

      getByEmail: (email: string) => {
        return get().bloomies[email];
      },

      mustRefreshBloomie: (email: string) => {
        const { bloomies, fetchedAtByEmail } = get();
        const cached = bloomies[email];
        const fetchedAt = fetchedAtByEmail[email] ?? null;

        if (!cached || fetchedAt === null) return true;
        const now = Date.now();
        const elapsed = now - fetchedAt;
        return elapsed > CACHE_TTL_MS;
      },

      reset: () => set({ bloomies: {}, fetchedAtByEmail: {} }),
    }),
    {
      name: 'bloomays-bloomies',
      partialize: (state) => ({ bloomies: state.bloomies, fetchedAtByEmail: state.fetchedAtByEmail }),
    },
  ),
);

export default useBloomiesStore;
